import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase, Heart, MapPin, Upload, Search, CheckCircle2,
  Shield, Users, Bell, ArrowRight, Menu, X,
} from 'lucide-react';

const ROLE_CARDS = [
  'Dental Assistant',
  'Registered Dental Assistant',
  'Dental Hygienist',
  'Office Manager',
  'Front Desk',
  'Treatment Coordinator',
  'Billing Specialist',
  'Orthodontic Assistant',
  'Oral Surgery Assistant',
  'Pediatric Dental Assistant',
  'Sterilization Technician',
  'And more...',
];

export default function JobsLandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchZip, setSearchZip] = useState('');

  const openSignup = () => {
    // For now, just route to /jobs/signup (will build next)
    window.location.href = '/jobs/signup';
  };

  const openLogin = () => {
    window.location.href = '/jobs/login';
  };

  const handleZipSearch = (e) => {
    e?.preventDefault?.();
    // Route to signup pre-populated with their zip — waitlist mode until jobs exist
    const zip = (searchZip || '').trim();
    window.location.href = zip
      ? `/jobs/signup?zip=${encodeURIComponent(zip)}`
      : '/jobs/signup';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="jobs-landing-page">
      {/* ============ COLORFUL CONCEPT HEADER ============ */}
      <div className="h-1.5 bg-gradient-to-r from-pink-500 via-purple-500 via-blue-500 via-cyan-400 to-emerald-400 animate-pulse" />

      <nav className="bg-gradient-to-r from-indigo-700 via-blue-700 to-cyan-600 sticky top-0 z-40 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="https://practcom.com" className="flex items-center gap-3 group">
            {/* Custom logo mark: tooth silhouette with a hidden briefcase notch */}
            <div className="relative w-12 h-12 rounded-2xl bg-white shadow-lg flex items-center justify-center group-hover:rotate-6 transition-transform">
              <svg viewBox="0 0 32 32" className="w-7 h-7" aria-hidden="true">
                <defs>
                  <linearGradient id="toothG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#2563eb" />
                  </linearGradient>
                </defs>
                <path
                  d="M16 3c-4 0-7 2-9 2s-3 2-3 5c0 4 2 7 3 12 .6 3 1.8 7 4 7 1.5 0 2-2 2.5-4.5S14.5 21 16 21s2 1 2.5 3.5S20 29 21.5 29c2.2 0 3.4-4 4-7 1-5 3-8 3-12 0-3-1-5-3-5s-5-2-9-2z"
                  fill="url(#toothG)"
                />
                {/* Briefcase handle on top - subtle dental + career fusion */}
                <rect x="13" y="2" width="6" height="2" rx="1" fill="#ffffff" opacity="0.6" />
              </svg>
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
            </div>
            <div className="leading-tight">
              <div className="text-white font-extrabold text-xl tracking-tight">
                DentalStaff
              </div>
              <div className="text-cyan-200 text-[11px] uppercase tracking-widest font-semibold">
                by PractCom
              </div>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={openLogin}
              data-testid="nav-signin-btn"
              className="px-5 py-2.5 text-sm font-semibold text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={openSignup}
              data-testid="nav-signup-btn"
              className="px-5 py-2.5 text-sm font-bold bg-white text-blue-700 rounded-lg hover:bg-cyan-50 hover:-translate-y-0.5 transition-all shadow-md"
            >
              Create Free Account
            </button>
          </div>

          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/20 bg-blue-800/95 backdrop-blur">
            <div className="px-4 py-3 flex flex-col gap-2">
              <button
                onClick={openLogin}
                className="w-full px-4 py-3 text-left text-sm font-semibold text-white/90 hover:bg-white/10 rounded-lg"
              >
                Sign In
              </button>
              <button
                onClick={openSignup}
                className="w-full px-4 py-3 text-center text-sm font-bold bg-white text-blue-700 rounded-lg"
              >
                Create Free Account
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* ============ LAUNCH BANNER ============ */}
      <div className="bg-gradient-to-r from-amber-50 to-amber-100 border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-center text-sm sm:text-base text-amber-900">
          <span className="mr-2" aria-hidden="true">🦷</span>
          <strong>We're just getting started!</strong> Create your profile now and be the first
          notified when positions open in your area.
        </div>
      </div>

      {/* ============ HERO — Branded Image Banner ============ */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Full-bleed hero image with overlaid interactive CTA */}
          <div className="relative w-full">
            <img
              src="/jobs-assets/hero-banner.png"
              alt="Dental Staff by PractCom — Apply online for dental office opportunities"
              className="w-full h-auto block"
              data-testid="hero-banner-image"
            />
            {/* Invisible click-through overlay on the "Find Your Next Opportunity" button in the image.
                Positioned as percentage of the image so it scales correctly at every screen size. */}
            <button
              onClick={openSignup}
              data-testid="hero-find-opportunity-btn"
              aria-label="Find Your Next Opportunity"
              className="absolute hover:bg-white/10 transition-colors rounded-full focus:outline-none focus:ring-4 focus:ring-cyan-300"
              style={{ left: '5.5%', top: '57%', width: '28%', height: '8%' }}
            />
          </div>

          {/* Action band below the image — keyboard/screen-reader accessible CTAs */}
          <div className="px-4 sm:px-6 lg:px-8 py-8 sm:py-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 border-b border-slate-200">
            <button
              onClick={openSignup}
              data-testid="hero-create-account-btn"
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-bold text-lg rounded-full shadow-lg shadow-cyan-500/30 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
            >
              Create Your Free Profile
              <ArrowRight className="w-5 h-5" />
            </button>

            <form
              onSubmit={handleZipSearch}
              className="w-full sm:w-auto bg-slate-50 border-2 border-slate-200 rounded-full p-1.5 flex items-center gap-1"
            >
              <div className="flex items-center pl-3 text-slate-400">
                <Search className="w-4 h-4" />
              </div>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={5}
                value={searchZip}
                onChange={(e) => setSearchZip(e.target.value.replace(/\D/g, ''))}
                placeholder="Your ZIP code"
                data-testid="hero-zip-input"
                className="bg-transparent py-2 px-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none w-32"
              />
              <button
                type="submit"
                data-testid="hero-zip-search-btn"
                className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-full"
              >
                Search
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="bg-white py-20" id="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center text-slate-900">
            How It Works
          </h2>
          <p className="mt-3 text-center text-slate-600 text-lg">Three simple steps. No fees, ever.</p>

          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {[
              {
                n: '1',
                icon: <Upload className="w-7 h-7 text-blue-700" />,
                title: 'Create Your Free Profile',
                desc: 'Upload your resume and set your job preferences. Takes less than 5 minutes.',
              },
              {
                n: '2',
                icon: <Search className="w-7 h-7 text-cyan-600" />,
                title: 'Browse Local Openings',
                desc: 'Filter by role, location, pay, and schedule. See only positions that match.',
              },
              {
                n: '3',
                icon: <CheckCircle2 className="w-7 h-7 text-emerald-600" />,
                title: 'Apply in One Click',
                desc: 'Your info goes directly to the dental practice — no middleman.',
              },
            ].map((s) => (
              <div
                key={s.n}
                className="relative bg-slate-50 rounded-2xl p-7 border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <span className="absolute -top-4 left-7 px-3 py-1 rounded-full bg-blue-700 text-white text-xs font-bold">
                  STEP {s.n}
                </span>
                <div className="mt-2 mb-4 w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center">
                  {s.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHO THIS IS FOR ============ */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center text-slate-900">
            Who This Is For
          </h2>
          <p className="mt-3 text-center text-slate-600 text-lg">
            Every dental role — from chairside to front desk.
          </p>
          <div className="mt-12 flex flex-wrap gap-3 justify-center max-w-4xl mx-auto">
            {ROLE_CARDS.map((role) => (
              <span
                key={role}
                className="px-5 py-3 bg-white border-2 border-slate-200 rounded-full text-sm font-semibold text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-all cursor-default"
              >
                {role}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TRUST ============ */}
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center text-slate-900">
            Why Dental Pros Choose Us
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 gap-x-10 gap-y-6">
            {[
              { icon: <Heart className="w-6 h-6 text-rose-600" />, text: '100% Free for Job Seekers — Always' },
              { icon: <Shield className="w-6 h-6 text-emerald-600" />, text: 'Real Dental Practices, Real Opportunities' },
              { icon: <Users className="w-6 h-6 text-blue-700" />, text: 'Created by a Dentist for Dentists' },
              { icon: <Bell className="w-6 h-6 text-amber-600" />, text: 'Your Information Is Never Sold' },
            ].map((b, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0">
                  {b.icon}
                </div>
                <p className="text-lg font-semibold text-slate-800 pt-1.5">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-gradient-to-br from-blue-700 via-blue-800 to-cyan-700 py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
            Ready to find your next role?
          </h2>
          <p className="mt-4 text-lg text-blue-100">
            Free to join. No credit card. Zero spam. Real positions at verified dental practices.
          </p>
          <button
            onClick={openSignup}
            data-testid="cta-create-account-btn"
            className="mt-8 px-8 py-4 bg-white hover:bg-blue-50 text-blue-700 font-bold text-lg rounded-xl shadow-xl transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
          >
            Create Your Free Profile
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid sm:grid-cols-2 gap-6 items-center">
            <div>
              <p className="text-white font-bold text-lg">DentalStaff by PractCom</p>
              <p className="text-sm mt-1">© {new Date().getFullYear()} The Oncall Bot LLC. All rights reserved.</p>
            </div>
            <div className="flex sm:justify-end flex-wrap gap-x-6 gap-y-2 text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <a href="mailto:admin@practcom.com" className="hover:text-white transition-colors">Contact Us</a>
              <a href="https://practcom.com" className="hover:text-white transition-colors">
                For dental practices: PractCom →
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
